/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography
} from '@mui/material';
import { formatDateTimeToTime12h } from '../utils/dateTimeUtils';

const API_BASE = process.env.REACT_APP_API_BASE || '';

// A reusable webcam component:
// - Preview can be mirrored (CSS only) without affecting captured frames
// - Supports multiple resolutions via getUserMedia constraints
// - Captures a single frame and posts to /api/recognize-face

const PRESETS = {
  low: { width: { ideal: 640 }, height: { ideal: 480 } },
  medium: { width: { ideal: 1280 }, height: { ideal: 720 } },
  high: { width: { ideal: 1920 }, height: { ideal: 1080 } },
  auto: { width: { ideal: 800 }, height: { ideal: 600 } },
};

const MANUAL_FALLBACK_FAILURE_THRESHOLD = 10;
const RECOGNITION_RETRY_COOLDOWN_MS = 2500;

export default function WebcamCapture({ targetEmployeeCode = '', targetUserId = '', hasOpenShift = false, onTimeInSaved, onTimeOutSaved, onCameraAvailabilityChange, onManualFallbackEligibilityChange, disabled = false }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const toastTimerRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [preset, setPreset] = useState('auto');
  const [mirrored, setMirrored] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [distance, setDistance] = useState(null);
  const [threshold, setThreshold] = useState(null);
  const [facesDetected, setFacesDetected] = useState(null);
  const [liveMode, setLiveMode] = useState(false);
  const liveModeRef = useRef(false);
  const recognitionCooldownUntilRef = useRef(0);
  const [lastEmployee, setLastEmployee] = useState(null);
  const [captureCount, setCaptureCount] = useState(0);
  const [failedRecognitionCount, setFailedRecognitionCount] = useState(0);
  const [lastFailureReason, setLastFailureReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [recognizedEmployeeId, setRecognizedEmployeeId] = useState(null);
  const [timeInLabel, setTimeInLabel] = useState('');
  const [timeInBusy, setTimeInBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [faceServiceOfflineUntil, setFaceServiceOfflineUntil] = useState(0);
  const [faceServiceWarned, setFaceServiceWarned] = useState(false);
  const [cameraAvailability, setCameraAvailability] = useState('checking');
  const faceServiceOffline = faceServiceOfflineUntil && Date.now() < faceServiceOfflineUntil;
  const triggerFaceServiceOffline = () => {
    setFaceServiceOfflineUntil(Date.now() + 10000);
    if (!faceServiceWarned) {
      setFaceServiceWarned(true);
      setToast({ type: 'warning', text: 'Face service is offline. Please ask an administrator to start it.' });
    }
  };

  const showToast = (text, tone = 'success') => {
    setToast({ text, tone });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 3500);
  };

  const getFaceStatusText = (count) => {
    if (count === 1) return 'Face detected: 1';
    if (count > 1) return 'Multiple faces detected';
    if (count === 0) return 'No face detected';
    return null;
  };

  const updateCameraAvailability = (nextState) => {
    setCameraAvailability(nextState);
    if (typeof onCameraAvailabilityChange === 'function') {
      try { onCameraAvailabilityChange(nextState); } catch (e) {}
    }
  };

  const registerRecognitionFailure = (reason = 'recognition_failed') => {
    setLastFailureReason(reason);
    setFailedRecognitionCount((prev) => {
      const next = prev + 1;
      const unlocked = next >= MANUAL_FALLBACK_FAILURE_THRESHOLD;
      if (unlocked && next === MANUAL_FALLBACK_FAILURE_THRESHOLD) {
        showToast('Face recognition failed repeatedly. Manual attendance is now available for HR review.', 'warning');
      }
      return next;
    });
  };

  const resetRecognitionFailures = () => {
    setFailedRecognitionCount(0);
    setLastFailureReason('');
  };

  useEffect(() => {
    if (typeof onManualFallbackEligibilityChange !== 'function') return;
    const eligible = failedRecognitionCount >= MANUAL_FALLBACK_FAILURE_THRESHOLD;
    const reason = eligible ? lastFailureReason : '';
    try {
      onManualFallbackEligibilityChange({
        eligible,
        reason,
        failedAttempts: failedRecognitionCount
      });
    } catch (e) {}
  }, [failedRecognitionCount, lastFailureReason, onManualFallbackEligibilityChange]);

  const getFailureReasonLabel = (reason) => {
    const normalized = String(reason || '').trim().toLowerCase();
    if (normalized === 'not_recognized') return 'Not recognized';
    if (normalized === 'wrong_employee') return 'Wrong employee';
    if (normalized === 'no_face') return 'No face detected';
    if (normalized === 'no_face_detected') return 'No face detected';
    if (normalized === 'multiple_faces') return 'Multiple faces detected';
    if (normalized === 'no_encodings') return 'No registered face data';
    if (normalized === 'employee_context_missing') return 'Employee face profile missing';
    if (normalized === 'session_missing') return 'Login session missing';
    if (normalized === 'role_not_allowed') return 'Face recognition not allowed';
    if (normalized === 'service_offline') return 'Face service unavailable';
    if (normalized === 'recognition_error' || normalized === 'recognition_failed') return 'Recognition failed';
    return '';
  };

  const normalizeRecognitionCode = (payload) => String(payload?.code || '').trim().toUpperCase();

  const getRecognitionFailureMessage = (payload) => {
    const code = normalizeRecognitionCode(payload);
    if (code === 'NO_FACE' || code === 'NO_FACE_DETECTED') return 'No face detected. Center your face in the frame and try again.';
    if (code === 'MULTIPLE_FACES') return 'Multiple faces detected. Make sure only one face is visible.';
    if (code === 'NO_ENCODINGS') return 'No registered face data was found for this account. Upload face photos first.';
    if (code === 'NO_MATCH' || code === 'FACE_NOT_RECOGNIZED') return 'Face detected but not recognized. Improve lighting, hold still, or try a clearer angle.';
    if (code === 'EMPLOYEE_CONTEXT_MISSING') return payload?.message || 'Unable to resolve the logged-in employee for face recognition.';
    if (code === 'UNAUTHORIZED') return payload?.message || 'Your login session was not found. Sign in again and retry face recognition.';
    if (code === 'FACE_RECOGNITION_NOT_ALLOWED') return payload?.message || 'Face recognition is available only for custodian accounts.';
    return payload?.message || 'Face recognition could not confirm your identity. Please try again.';
  };

  const armRecognitionCooldown = () => {
    recognitionCooldownUntilRef.current = Date.now() + RECOGNITION_RETRY_COOLDOWN_MS;
  };

  const getPrimaryStatusText = () => {
    if (confirmOpen) return hasOpenShift ? 'Face verified for time out' : 'Face verified';
    if (cameraAvailability === 'unavailable') return 'No camera available';
    if (faceServiceOffline) return 'Face service unavailable';
    if (lastFailureReason) return getFailureReasonLabel(lastFailureReason);
    if (message) return message;
    return 'Ready for face check';
  };

  const getSecondaryStatusText = () => {
    if (confirmOpen) return hasOpenShift ? 'Confirm to save your time out.' : 'Confirm to save your attendance.';
    if (cameraAvailability === 'unavailable') return 'Use the manual request so HR can review your attendance.';
    if (faceServiceOffline) return 'Please try again later or contact an administrator.';
    if (lastFailureReason === 'no_face') return 'Center your face in the frame.';
    if (lastFailureReason === 'multiple_faces') return 'Only one face should be visible to the camera.';
    if (lastFailureReason === 'no_encodings') return 'Register face photos for this account before using face attendance.';
    if (lastFailureReason === 'employee_context_missing') return 'The logged-in account is missing employee face context.';
    if (lastFailureReason === 'wrong_employee') return 'Use the assigned account for this attendance.';
    if (lastFailureReason === 'not_recognized') return 'Try better lighting or hold still before trying again.';
    if (lastFailureReason === 'service_offline') return 'Manual fallback may still be used if HR review is needed.';
    if (message && message.toLowerCase().includes('camera permission')) return 'Allow camera access and try again.';
    if (message && message.toLowerCase().includes('camera is unavailable')) return 'Close other camera apps, then try again.';
    return 'Use a clear, front-facing view.';
  };

  const normalizeEmployeeCode = (value) => String(value || '').trim().replace(/^0+/, '');
  const normalizeUserId = (value) => String(value || '').trim();
  const doesRecognizedIdMatchTarget = (recognizedId) => {
    if (recognizedId === undefined || recognizedId === null || recognizedId === '') return false;
    const recognizedRaw = normalizeUserId(recognizedId);
    const targetUserRaw = normalizeUserId(targetUserId);
    if (targetUserRaw) {
      return recognizedRaw === targetUserRaw;
    }
    const targetCodeRaw = normalizeEmployeeCode(targetEmployeeCode);
    if (targetCodeRaw) {
      return normalizeEmployeeCode(recognizedRaw) === targetCodeRaw;
    }
    return true;
  };

  const formatLocalTime = (dt) => {
    try {
      return formatDateTimeToTime12h(dt);
    } catch (e) {
      return '';
    }
  };

  async function startCamera(kind = preset) {
    stopCamera();
    setMessage('');
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      const cams = list.filter((d) => d.kind === 'videoinput');
      setDevices(cams);
      if (!deviceId && cams.length > 0) setDeviceId(cams[0].deviceId || '');
      if (cams.length === 0) {
        updateCameraAvailability('unavailable');
        setMessage('No camera detected on this device. You may use the HR-reviewed no-camera time-in request.');
        return;
      }
      updateCameraAvailability('available');
    } catch (e) {
      // ignore enumerate failure and let getUserMedia decide
    }
    const constraints = { video: { ...PRESETS[kind], facingMode: 'user' }, audio: false };
    if (deviceId) constraints.video.deviceId = { exact: deviceId };
    try {
      const s = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play();
      }
      setStream(s);
      updateCameraAvailability('available');
    } catch (e) {
      if (e?.name === 'AbortError') {
        return;
      }
      console.error('getUserMedia failed:', e);
      if (e?.name === 'NotFoundError' || e?.name === 'DevicesNotFoundError') {
        updateCameraAvailability('unavailable');
        setMessage('No camera detected on this device. You may use the HR-reviewed no-camera time-in request.');
      } else if (e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError') {
        updateCameraAvailability('available');
        setMessage('Camera permission was blocked. Allow camera access and try again.');
      } else if (e?.name === 'NotReadableError' || e?.name === 'TrackStartError' || e?.name === 'AbortError') {
        updateCameraAvailability('available');
        setMessage('Camera is unavailable or busy. Close other camera apps, then try again.');
      } else {
        updateCameraAvailability('available');
        setMessage('Camera access failed. Please check the device and try again.');
      }
    }
  }

  function stopCamera() {
    try {
      ((stream && stream.getTracks()) || []).forEach(t => t.stop());
    } catch {}
    setStream(null);
  }

  useEffect(() => {
    (async () => {
      try {
        await startCamera('auto');
        // Enumerate devices after first permission grant
        const list = await navigator.mediaDevices.enumerateDevices();
        const cams = list.filter(d => d.kind === 'videoinput');
        setDevices(cams);
        if (!deviceId && cams.length > 0) setDeviceId(cams[0].deviceId || '');
      } catch (_) {}
    })();
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    liveModeRef.current = liveMode;
  }, [liveMode]);

  const handleMirrorToggle = () => {
    setMirrored((prev) => !prev);
  };

  async function handlePresetChange(e) {
    const v = e.target.value;
    setPreset(v);
    await startCamera(v);
  }

  async function captureAndRecognize() {
    if (disabled) return;
    if (Date.now() < recognitionCooldownUntilRef.current) {
      setMessage('Please wait a moment before trying face recognition again.');
      return;
    }
    if (faceServiceOffline) {
      setMessage('Face recognition temporarily paused because the face service is offline.');
      return;
    }
    if (!videoRef.current) return;
    setBusy(true);
    setMessage('');
    setDistance(null);
    setConfirmOpen(false);
    setRecognizedEmployeeId(null);
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current || document.createElement('canvas');
      const w = video.videoWidth || 800;
      const h = video.videoHeight || 600;
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');

      // Draw the actual frame as-is (not mirrored). The preview may be mirrored via CSS.
      ctx.drawImage(video, 0, 0, w, h);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
      if (!blob) throw new Error('Failed to capture image');
      const form = new FormData();
      form.append('photo', blob, 'capture.jpg');

      const resp = await axios.post(`${API_BASE}/api/recognize-face`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        validateStatus: () => true,
      });
      if (resp.status === 503 && resp.data?.error === 'FACE_SERVICE_UNAVAILABLE') {
        triggerFaceServiceOffline();
        setMessage('Face recognition is unavailable right now because the face service is offline.');
        setLastEmployee(null);
        registerRecognitionFailure('service_offline');
        return;
      }
      const d = resp?.data || {};
      const recognitionCode = normalizeRecognitionCode(d);
      setFacesDetected(typeof d.faces_found === 'number' ? d.faces_found : null);
      setThreshold(typeof d.tolerance === 'number' ? d.tolerance : null);
      if (d && d.ok && d.code === 'RECOGNIZED') {
        if (typeof d.best_distance === 'number') setDistance(d.best_distance);
        const recognizedId = d.employee_id || d.employeeId || d.name || null;
        setLastEmployee(recognizedId);
        if (recognizedId) {
          const now = new Date();
          const isMatch = doesRecognizedIdMatchTarget(recognizedId);
          setMessage(isMatch ? (hasOpenShift ? 'Face verified. Confirm to save your time out.' : 'Face verified. Confirm to save your attendance.') : 'Face detected, but it does not match the logged-in employee.');
          setRecognizedEmployeeId(isMatch ? String(targetEmployeeCode || recognizedId) : null);
          setTimeInLabel(formatLocalTime(now));
          if (isMatch && liveModeRef.current) setLiveMode(false);
          setConfirmOpen(Boolean(isMatch));
          if (isMatch) {
            resetRecognitionFailures();
          } else {
            registerRecognitionFailure('wrong_employee');
          }
        }
      } else if (recognitionCode === 'NO_FACE' || recognitionCode === 'NO_FACE_DETECTED') {
        setFacesDetected(0);
        setMessage(getRecognitionFailureMessage(d));
        setLastEmployee(null);
        registerRecognitionFailure('no_face');
        armRecognitionCooldown();
      } else if (recognitionCode === 'MULTIPLE_FACES') {
        setMessage(getRecognitionFailureMessage(d));
        setLastEmployee(null);
        registerRecognitionFailure('multiple_faces');
        armRecognitionCooldown();
      } else if (recognitionCode === 'NO_ENCODINGS') {
        setMessage(getRecognitionFailureMessage(d));
        setLastEmployee(null);
        registerRecognitionFailure('no_encodings');
        armRecognitionCooldown();
      } else if (recognitionCode === 'NO_MATCH' || recognitionCode === 'FACE_NOT_RECOGNIZED') {
        setMessage(getRecognitionFailureMessage(d));
        if (typeof d.best_distance === 'number') setDistance(d.best_distance);
        setLastEmployee(null);
        registerRecognitionFailure('not_recognized');
        armRecognitionCooldown();
      } else if (recognitionCode === 'FACE_RECOGNITION_NOT_ALLOWED') {
        setMessage(getRecognitionFailureMessage(d));
        setLastEmployee(null);
        registerRecognitionFailure('role_not_allowed');
        armRecognitionCooldown();
      } else if (recognitionCode === 'EMPLOYEE_CONTEXT_MISSING') {
        setMessage(getRecognitionFailureMessage(d));
        setLastEmployee(null);
        registerRecognitionFailure('employee_context_missing');
        armRecognitionCooldown();
      } else if (recognitionCode === 'UNAUTHORIZED') {
        setMessage(getRecognitionFailureMessage(d));
        setLastEmployee(null);
        registerRecognitionFailure('session_missing');
        armRecognitionCooldown();
      } else {
        setMessage(getRecognitionFailureMessage(d));
        setLastEmployee(null);
        registerRecognitionFailure('recognition_failed');
        armRecognitionCooldown();
      }
    } catch (e) {
      console.error('recognize error:', e);
      if (e?.response?.status === 503 && e?.response?.data?.error === 'FACE_SERVICE_UNAVAILABLE') {
        triggerFaceServiceOffline();
        setMessage('Face recognition is unavailable right now because the face service is offline.');
        registerRecognitionFailure('service_offline');
        armRecognitionCooldown();
      } else {
      setMessage(getRecognitionFailureMessage(e?.response?.data || null) || 'Face recognition failed. Please try again.');
      registerRecognitionFailure('recognition_error');
      armRecognitionCooldown();
      }
      setLastEmployee(null);
    } finally {
      setBusy(false);
    }
  }

  const handleTimeIn = async () => {
    if (disabled) return;
    const employeeIdToUse = recognizedEmployeeId || targetEmployeeCode;
    if (!employeeIdToUse || timeInBusy) return;
    setTimeInBusy(true);
    try {
      console.info('Time-in request:', employeeIdToUse);
      const resp = await axios.post(`${API_BASE}/api/attendance/time-in`, { employeeid: String(employeeIdToUse) }, {
        withCredentials: true,
        validateStatus: () => true,
      });
      const data = resp && resp.data ? resp.data : {};
      if (resp.status >= 200 && resp.status < 300 && data.ok) {
        showToast('Time in saved', 'success');
        if (data.autoTimedOut) {
          showToast('Previous shift was auto time-out after 8 hours.', 'info');
        }
        setConfirmOpen(false);
        setRecognizedEmployeeId(null);
        setMessage('Time in saved');
        resetRecognitionFailures();
        if (typeof onTimeInSaved === 'function') {
          try { await onTimeInSaved(data); } catch (e) {}
        }
      } else {
        const code = data && data.code;
        if (code === 'OPEN_SHIFT_EXISTS') {
          showToast('Already timed in today. Please time-out first.', 'warning');
        } else {
          showToast('Failed to record time in. Please try again or contact your administrator.', 'error');
        }
      }
    } catch (e) {
      console.error('Time-in failed:', e);
      const code = e?.response?.data?.code;
      if (code === 'OPEN_SHIFT_EXISTS') {
        showToast('Already timed in today. Please time-out first.', 'warning');
      } else {
        showToast('Failed to record time in. Please try again or contact your administrator.', 'error');
      }
    } finally {
      setConfirmOpen(false);
      setRecognizedEmployeeId(null);
      setTimeInBusy(false);
    }
  };

  const handleTimeOut = async () => {
    if (disabled || timeInBusy) return;
    setTimeInBusy(true);
    try {
      console.info('[attendance][frontend][webcam-timeout] request', {});
      const resp = await axios.post(`${API_BASE}/api/attendance/time-out`, {}, {
        withCredentials: true,
        validateStatus: () => true,
      });
      const data = resp && resp.data ? resp.data : {};
      console.info('[attendance][frontend][webcam-timeout] response', data);
      if (resp.status >= 200 && resp.status < 300 && data.ok) {
        showToast(data.message || 'Time out recorded.', 'success');
        setMessage(data.message || 'Time out saved');
        resetRecognitionFailures();
        if (typeof onTimeOutSaved === 'function') {
          try { await onTimeOutSaved(data); } catch (e) {}
        }
      } else {
        showToast(data.message || data.error || 'Failed to record time out. Please try again or contact your administrator.', 'error');
      }
    } catch (e) {
      console.error('Time-out failed:', e);
      showToast(e?.response?.data?.message || e?.response?.data?.error || 'Failed to record time out. Please try again or contact your administrator.', 'error');
    } finally {
      setConfirmOpen(false);
      setRecognizedEmployeeId(null);
      setTimeInBusy(false);
    }
  };

  // Live auto-capture loop
  useEffect(() => {
    if (!liveMode) return;
    let cancelled = false;
    const loop = async () => {
      if (cancelled) return;
      if (!liveModeRef.current) return;
      if (!busy) {
        await captureAndRecognize();
        setCaptureCount(c => c + 1);
      }
      setTimeout(loop, facesDetected === 0 ? 1200 : 2000);
    };
    loop();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveMode, busy, facesDetected]);

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 12 }}>
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 1.5,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          bgcolor: '#f8fafc',
          borderRadius: 2,
          border: '1px solid #e2e8f0',
        }}
      >
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="wc-resolution-label">Resolution</InputLabel>
          <Select
            labelId="wc-resolution-label"
            label="Resolution"
            value={preset}
            onChange={handlePresetChange}
          >
            {Object.keys(PRESETS).map((key) => (
              <MenuItem key={key} value={key}>
                {key === 'low' && '640x480 (Low)'}
                {key === 'medium' && '1280x720 (Medium)'}
                {key === 'high' && '1920x1080 (High)'}
                {key === 'auto' && 'Auto'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="wc-camera-label">Camera Device</InputLabel>
          <Select
            labelId="wc-camera-label"
            label="Camera Device"
            value={deviceId}
            onChange={async (e) => { setDeviceId(e.target.value); await startCamera(preset); }}
          >
            {devices.length === 0 ? (
              <MenuItem value="">Default</MenuItem>
            ) : devices.map((d, idx) => (
              <MenuItem key={d.deviceId || idx} value={d.deviceId}>
                {d.label || `Camera ${idx + 1}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant={mirrored ? 'contained' : 'outlined'}
          onClick={handleMirrorToggle}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 999,
            px: 2.5,
            minHeight: 36,
            opacity: mirrored ? 1 : 0.65,
          }}
        >
          Mirror preview
        </Button>

        <Button
          variant="contained"
          onClick={captureAndRecognize}
          disabled={busy || disabled}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 999,
            px: 3,
          }}
        >
          {busy ? 'Capturing…' : 'Capture & Recognize'}
        </Button>

      </Paper>
      {toast && (
        <div style={{ marginBottom: 8, padding: '6px 10px', borderRadius: 6, fontSize: 13, color: '#111', background: toast.tone === 'success' ? '#dcfce7' : toast.tone === 'warning' ? '#fef9c3' : '#fee2e2' }}>
          {toast.text}
        </div>
      )}
      {cameraAvailability === 'available' && lastFailureReason && !confirmOpen && (
        <div style={{ marginBottom: 8, padding: '8px 10px', borderRadius: 8, fontSize: 13, color: '#7c2d12', background: '#ffedd5', border: '1px solid #fdba74' }}>
          Latest face check result: <strong>{getFailureReasonLabel(lastFailureReason)}</strong>
        </div>
      )}
      <div style={{ position: 'relative', width: '100%', maxWidth: 480 }}>
        <video
          ref={videoRef}
          playsInline
          muted
          style={{ width: '100%', transform: mirrored ? 'scaleX(-1)' : 'none', borderRadius: 8, background: '#000' }}
        />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        {liveMode && (
          <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,0.55)', color: '#fff', padding: '4px 8px', borderRadius: 6, fontSize: 12 }}>
            Live #{captureCount}
          </div>
        )}
        <div style={{ position: 'absolute', top: 8, right: 8 }}>
        {lastEmployee && doesRecognizedIdMatchTarget(lastEmployee) ? (
          <div style={{ background: '#16a34a', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 12, boxShadow: '0 0 4px rgba(0,0,0,0.4)' }}>MATCH {targetEmployeeCode}</div>
        ) : lastEmployee ? (
          <div style={{ background: '#f59e0b', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 12 }}>{targetEmployeeCode ? `OTHER: ${lastEmployee}` : `RECOGNIZED: ${lastEmployee}`}</div>
        ) : cameraAvailability === 'unavailable' ? (
          <div style={{ background: '#dc2626', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 12 }}>NO CAMERA</div>
        ) : facesDetected === 0 ? (
          <div style={{ background: '#dc2626', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 12 }}>NO FACE</div>
        ) : message && message.toLowerCase().includes('not recognized') ? (
          <div style={{ background: '#dc2626', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 12 }}>UNRECOGNIZED</div>
        ) : message && message.toLowerCase().includes('camera') ? (
          <div style={{ background: '#dc2626', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 12 }}>CAMERA ISSUE</div>
        ) : (
          <div style={{ background: '#475569', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 12 }}>READY</div>
        )}
        </div>
      </div>
      <div style={{ marginTop: 8, fontSize: 14 }}>
        <div style={{ fontWeight: 700 }}>
          {getPrimaryStatusText()}
        </div>
        <div style={{ color: cameraAvailability === 'unavailable' || faceServiceOffline ? '#b45309' : '#666', fontSize: 12, marginTop: 2 }}>
          {getSecondaryStatusText()}
        </div>
        {cameraAvailability === 'available' && failedRecognitionCount > 0 && (
          <div style={{ color: failedRecognitionCount >= MANUAL_FALLBACK_FAILURE_THRESHOLD ? '#b45309' : '#666', fontSize: 12, marginTop: 4 }}>
            Attempts: {failedRecognitionCount}/{MANUAL_FALLBACK_FAILURE_THRESHOLD}
            {failedRecognitionCount >= MANUAL_FALLBACK_FAILURE_THRESHOLD ? ' - Manual request available.' : ''}
          </div>
        )}
        {targetEmployeeCode ? (
          <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>ID: {targetEmployeeCode}</div>
        ) : null}
      </div>
      {confirmOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 16, width: '90%', maxWidth: 360, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Face Verified</div>
            <div style={{ fontSize: 14, marginBottom: 16 }}>{hasOpenShift ? 'Time out' : 'Time in'} at: {timeInLabel || formatLocalTime(new Date())}</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmOpen(false)} disabled={timeInBusy} style={{ padding: '6px 12px', background: '#e5e7eb', border: 'none', borderRadius: 6 }}>
                Cancel
              </button>
              <button onClick={hasOpenShift ? handleTimeOut : handleTimeIn} disabled={timeInBusy} style={{ padding: '6px 12px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6 }}>
                {timeInBusy ? 'Saving...' : hasOpenShift ? 'TIME OUT' : 'TIME IN'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
